import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import type {
    GetProfessionalDownloadFilesQuery,
    GetProfessionalDownloadFilesQueryVariables,
} from 'generated/graphql';
import { GetProfessionalDownloadFilesDocument } from 'generated/documents/GetProfessionalDownloadFilesDocument';

export const PROFESSIONAL_DOWNLOAD_FILES_API_ROUTE =
    '/remix-api/professional/download-files';

/** Guard against a hand-crafted link pointing the loader off the directory. */
const URL_SEGMENT_PATTERN = /^[\w-]+$/;

const EMPTY = { files: [] };

/**
 * The downloads owned by one item, fetched when its group opens.
 *
 * Groups are listed with a count and no file rows, so this is what turns a count
 * into rows. The current search and category filter ride along, so an opened group
 * shows the files its count promised rather than everything the item owns.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const urlSegment = url.searchParams.get('urlSegment') ?? '';
    const itemID = Number(url.searchParams.get('itemId') ?? '');
    const search = url.searchParams.get('q')?.trim() ?? '';
    const fileCategories = url.searchParams.getAll('fileCategory');

    if (!URL_SEGMENT_PATTERN.test(urlSegment) || !Number.isInteger(itemID)) {
        return routerData(EMPTY, { status: 400 });
    }

    try {
        const result = await context.client.query<
            GetProfessionalDownloadFilesQuery,
            GetProfessionalDownloadFilesQueryVariables
        >({
            query: GetProfessionalDownloadFilesDocument,
            variables: {
                URLSegment: urlSegment,
                ItemID: itemID,
                Search: search || null,
                FileCategories: fileCategories.length ? fileCategories : null,
            },
        });

        return routerData({
            files: result.data?.professionalDownloadFiles ?? [],
        });
    } catch (error) {
        logger.error('Error loading professional download files', {
            error,
            urlSegment,
            itemID,
        });
        // A failed group must not blow up the tab around it; the row reports the
        // failure and stays retryable.
        return routerData(EMPTY, { status: 500 });
    }
};

/** Never revalidate — this endpoint only serves explicit `fetcher.load` calls. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
